import { getCampaigns, triggerCampaign } from 'widget/api/campaign';
import campaignTimer from 'widget/helpers/campaignTimer';
import {
  formatCampaigns,
  filterCampaigns,
} from 'widget/helpers/campaignHelper';
import { isEmptyObject } from 'widget/helpers/utils';

const state = {
  records: [],
  uiFlags: {
    isError: false,
  },
  activeCampaigns: [],
  isDisplayingCampaigns: false,
  executedCampaignIds: [],
  campaignHasExecuted: false,
  selectedCampaignId: null,
};

const resetCampaignTimers = (
  campaigns,
  currentURL,
  websiteToken,
  isInBusinessHours
) => {
  const formattedCampaigns = formatCampaigns({ campaigns });
  // Find all campaigns that matches the current URL
  const filteredCampaigns = filterCampaigns({
    campaigns: formattedCampaigns,
    currentURL,
    isInBusinessHours,
  });
  campaignTimer.initTimers({ campaigns: filteredCampaigns }, websiteToken);
};

export const getters = {
  getCampaigns: $state => $state.records,
  getActiveCampaigns: $state => $state.activeCampaigns,
  getSelectedCampaign: $state => {
    if ($state.selectedCampaignId) {
      return (
        $state.activeCampaigns.find(
          campaign => campaign.id === $state.selectedCampaignId
        ) || {}
      );
    }
    return {};
  },
  isDisplayingCampaigns: $state => $state.isDisplayingCampaigns,
  isCampaignExecuted: $state => campaignId =>
    $state.executedCampaignIds.includes(campaignId),
  getSelectedCampaignId: $state => $state.selectedCampaignId,
};

export const actions = {
  fetchCampaigns: async (
    { commit },
    { websiteToken, currentURL, isInBusinessHours }
  ) => {
    try {
      const { data: campaigns } = await getCampaigns(websiteToken);
      commit('setCampaigns', campaigns);
      commit('setError', false);
      resetCampaignTimers(
        campaigns,
        currentURL,
        websiteToken,
        isInBusinessHours
      );
    } catch (error) {
      commit('setError', true);
    }
  },
  initCampaigns: async (
    { getters: { getCampaigns: campaigns }, dispatch },
    { currentURL, websiteToken, isInBusinessHours }
  ) => {
    if (!campaigns.length) {
      dispatch('fetchCampaigns', {
        websiteToken,
        currentURL,
        isInBusinessHours,
      });
    } else {
      resetCampaignTimers(
        campaigns,
        currentURL,
        websiteToken,
        isInBusinessHours
      );
    }
  },
  startCampaign: async (
    { commit, getters: gettersObj },
    { websiteToken, campaignId }
  ) => {
    // Only check if the campaign has already been executed
    // DO NOT check isWidgetOpen for time-based campaigns
    if (gettersObj.isCampaignExecuted(campaignId)) {
      return;
    }

    try {
      const { data: campaigns } = await getCampaigns(websiteToken);
      // Check campaign is disabled or not
      const campaign = campaigns.find(item => item.id === campaignId);
      if (!isEmptyObject(campaign)) {
        // Important: Add campaign to activeCampaigns regardless of whether other campaigns
        // have already been added before
        commit('addActiveCampaign', campaign);
      }
    } catch (error) {
      commit('setError', true);
    }
  },

  executeCampaign: async (
    { commit, getters: gettersObj },
    { campaignId, websiteToken, customAttributes, userMessage }
  ) => {
    // Skip if campaign is already executed
    if (gettersObj.isCampaignExecuted(campaignId)) {
      return;
    }

    try {
      commit(
        'conversation/setConversationUIFlag',
        { isCreating: true },
        { root: true }
      );
      await triggerCampaign({
        campaignId,
        websiteToken,
        customAttributes,
        userMessage,
      });
      // Mark this specific campaign as executed
      commit('addExecutedCampaign', campaignId);
      // Remove it from active campaigns
      commit('removeActiveCampaign', campaignId);
    } catch (error) {
      commit('setError', true);
    } finally {
      commit(
        'conversation/setConversationUIFlag',
        { isCreating: false },
        { root: true }
      );
    }
  },
  resetCampaign: async ({ commit }) => {
    try {
      commit('resetAllCampaigns');
    } catch (error) {
      commit('setError', true);
    }
  },
  setCampaignDisplayStatus: ({ commit }, status) => {
    commit('setDisplayingCampaigns', status);
  },
  setSelectedCampaign: ({ commit }, campaignId) => {
    commit('setSelectedCampaignId', campaignId);
  },
};

export const mutations = {
  setCampaigns($state, data) {
    $state.records = data;
  },
  addActiveCampaign($state, campaign) {
    const existingIndex = $state.activeCampaigns.findIndex(
      c => c.id === campaign.id
    );

    if (existingIndex === -1) {
      $state.activeCampaigns.push(campaign);

      if (!$state.isDisplayingCampaigns) {
        $state.isDisplayingCampaigns = true;
      }
    }
  },
  removeActiveCampaign($state, campaignId) {
    const index = $state.activeCampaigns.findIndex(c => c.id === campaignId);
    if (index !== -1) {
      $state.activeCampaigns.splice(index, 1);

      if ($state.activeCampaigns.length === 0) {
        $state.isDisplayingCampaigns = false;
      }
    }
  },
  resetActiveCampaigns($state) {
    $state.activeCampaigns = [];
    $state.isDisplayingCampaigns = false;
  },
  setDisplayingCampaigns($state, status) {
    $state.isDisplayingCampaigns = status;
  },
  setError($state, value) {
    $state.uiFlags.isError = value;
  },
  setHasFetched($state, value) {
    $state.uiFlags.hasFetched = value;
  },
  setCampaignExecuted($state, data) {
    // For backward compatibility with old code
    // If data is true, we mark all current active campaigns as executed
    // If data is false, we clear the executed campaigns list
    if (data === true) {
      $state.activeCampaigns.forEach(campaign => {
        if (!$state.executedCampaignIds.includes(campaign.id)) {
          $state.executedCampaignIds.push(campaign.id);
        }
      });
    } else if (data === false) {
      $state.executedCampaignIds = [];
    }
    // Keep this for backward compatibility
    $state.campaignHasExecuted = data;
  },
  addExecutedCampaign($state, campaignId) {
    if (!$state.executedCampaignIds.includes(campaignId)) {
      $state.executedCampaignIds.push(campaignId);
    }
  },
  resetAllCampaigns($state) {
    $state.activeCampaigns = [];
    $state.executedCampaignIds = [];
    $state.isDisplayingCampaigns = false;
    $state.campaignHasExecuted = false;
    $state.selectedCampaignId = null;
  },
  setSelectedCampaignId($state, campaignId) {
    $state.selectedCampaignId = campaignId;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
